// O RODAPÉ — um só, para o produto inteiro.
//
// Seis pontos do T2 pedem este arquivo, e a metade deles é sobre o rodapé **não existir** onde
// deveria:
//
//   T2.9   "tinha questões de LGPD para os dados que você fornece, tinham outras coisas no rodapé
//           que são importantes de ter mesmo"
//   T2.10  "e aqui só tem o quê? A biblioteca que a gente já tem em cima, repete um monte de coisa"
//   T2.11  "o rodapé seria talvez para colocar esses manuais um pouco mais chatos, termos de uso,
//           LGPD, coisa que a pessoa precisa conseguir acessar, mas que não necessariamente
//           precisa ter um destaque de ficar lá em cima"
//   T2.12  "não sei onde acessar essas informações para começo de conversa. Se eu quiser checar
//           LGPD disso aqui, não tenho como checar."
//   T2.13  "e aí repetiu umas coisas que tá no cabeçalho embaixo"
//   T2.14  "pode usar as outras plataformas como estão agora, com as alterações que a gente pediu
//           DE MODELO para alterar esse rodapé"
//
// ── O QUE ESTAVA ERRADO, E ERA PIOR DO QUE A QUEIXA DIZIA ────────────────────────────────────
//
// Medido em 2026-09-12: havia rodapé em UMA tela de catorze — dentro do `inicio.tsx`, como markup,
// não como componente. E os cinco links dele eram: Biblioteca, Quem somos, Objetivos, Parceiros e
// "Ver a versão clássica". Quatro dos cinco **também estão no cabeçalho** (`nav.tsx`), que é o
// T2.13 na letra: o rodapé era uma segunda cópia do menu.
//
// Isso tinha uma consequência que o T2 só revela quando se lê os planos juntos: o `PL-T2.4` pede
// que "quem somos, objetivos e parceiros" DESÇAM para o rodapé mantendo o acesso por "um
// botãozinho" (T2.29). Com rodapé em uma tela só, descer significaria **sumir de treze**. Por isso
// este componente vem antes daquele trabalho, e não depois.
//
// ── O MODELO É DE FORA, E É O QUE ELE PEDIU ─────────────────────────────────────────────────
//
// `T2.14` manda usar as outras plataformas da casa como modelo. Fui olhar, e o
// `xequemath/app/components/rodape.tsx` é exatamente isto — um componente único, com a lição já
// paga no comentário dele: *"havia três rodapés diferentes copiados à mão, todos com as mesmas
// duas linhas e nenhum link. Rodapé copiado é rodapé que diverge."*
//
// O comentário de lá cita a MESMA queixa, em outro produto: *"Diferente do EducaHub Play, não tem
// um rodapé com aquelas informações de LGPD, de termos de uso, etc."* A queixa é reincidente na
// casa, e a solução dela já existia — em outro repositório.
//
// ── POR QUE ESTE ARQUIVO NÃO REPETE O CABEÇALHO ─────────────────────────────────────────────
//
// A régua é dele, e aparece duas vezes: *"coisa que a pessoa precisa conseguir acessar, mas que
// não necessariamente precisa ter um destaque de ficar lá em cima, porque não é coisa que a pessoa
// vai acessar sempre"* (T2.11) e *"a gente tá querendo apostar mais coisas que são funcionais"*
// (T2.28, do PL-T2.4). **Frequência de uso decide altura na página.** Então:
//
//   em cima  o que se usa toda hora     Biblioteca · Criar · Meus quadrinhos · Minha conta
//   embaixo  o que se acessa uma vez    institucional, LGPD, termos, versão clássica
//
// "Biblioteca" é a única coisa que aparece nos dois, e de propósito: ela é o destino mais usado do
// produto e também o que um visitante deslogado procura primeiro.

import { Link, useLocation } from "react-router-dom";

import { CARTILHAS } from "./cartilhas";
import "./rodape.scss";

/** Cada grupo é uma coluna. Separar por assunto em vez de uma fileira longa: com nove links numa
 *  linha só, ninguém acha nada — e era parte do "não sei onde acessar essas informações".
 *  `externo` marca o link que leva para fora da plataforma (as cartilhas): vira `<a>` e abre em
 *  outra aba, como no cabeçalho. */
const GRUPOS: { titulo: string; itens: { para: string; texto: string; externo?: boolean }[] }[] = [
  {
    titulo: "O projeto",
    itens: [
      { para: "/quem-somos", texto: "Quem somos" },
      { para: "/objetivos", texto: "Objetivos" },
      { para: "/parceiros", texto: "Parceiros" },
    ],
  },
  {
    titulo: "Seus dados",
    itens: [
      { para: "/lgpd", texto: "LGPD" },
      { para: "/privacidade", texto: "Privacidade" },
      { para: "/termos", texto: "Termos de uso" },
    ],
  },
  {
    titulo: "A plataforma",
    itens: [
      { para: "/biblioteca", texto: "Biblioteca" },
      { para: CARTILHAS.para, texto: CARTILHAS.rotulo, externo: true },
      { para: "/classico", texto: "Ver a versão clássica" },
      // T2.92: "uma coisa que eu NAO ACHEI aqui e uma estrutura de canvas diferente."
      //
      // Os quatro motores existem e desenham desde 09/09, e `/motores` os mostra lado a lado —
      // a tela foi construida para que "o formato suporta quatro motores" fosse VERIFICAVEL.
      // Medido em 12/09: ela nao estava em menu nenhum. Ele nao achou porque nao dava para achar.
      //
      // Fica no rodape e nao no cabecalho pela regua dele mesmo (T2.11): o que se acessa uma vez
      // mora embaixo. Isto e material de quem AVALIA a ferramenta, e o T2.98 pede exatamente
      // isso — "eu tenho que validar qual canvas ta sendo usado e qual e o melhor caminho".
      { para: "/motores", texto: "Os formatos de página" },
    ],
  },
];

function Rodape() {
  return (
    <footer className="hq-rodape">
      <div className="hq-rodape-conteudo">
        <div className="hq-rodape-marca">
          <strong>HQ-LAB</strong>
          <span>GEOTEC</span>
          <span>Universidade do Estado da Bahia — UNEB</span>
        </div>

        {GRUPOS.map((g) => (
          <nav className="hq-rodape-grupo" key={g.titulo} aria-label={g.titulo}>
            {/* `<h2>` e não `<strong>`: são três grupos de links, e um leitor de tela que pula
                por cabeçalho precisa conseguir escolher o grupo em vez de ouvir os nove. */}
            <h2>{g.titulo}</h2>
            <ul>
              {g.itens.map((i) => (
                <li key={i.para}>
                  {/* `<Link>`, que vira `<a href>`: navegar é o que `<a>` significa, e é o que faz
                      abrir em aba nova, copiar endereço e leitor de tela anunciarem certo. Era a
                      decisão do rodapé antigo e ela continua. */}
                  {i.externo ? (
                    <a href={i.para} target="_blank" rel="noopener noreferrer">
                      {i.texto}
                      <span className="visualmente-oculto"> (abre em outra aba)</span>
                    </a>
                  ) : (
                    <Link to={i.para}>{i.texto}</Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>
    </footer>
  );
}

/**
 * ONDE O RODAPÉ NÃO APARECE, e por que a lista é curta e explícita.
 *
 * T2.11/T2.12 pedem o rodapé em toda tela — é isso que faz LGPD e termos serem "alcançáveis". A
 * exceção é o EDITOR, e não é preguiça: ele é uma mesa de trabalho que ocupa a tela inteira, com
 * rolagem própria da folha. Um rodapé embaixo dele empurraria o canvas e acrescentaria uma segunda
 * rolagem — o T1.40 do pedido, que foi consertado à mão, voltando pela porta da frente.
 *
 * A lista é de prefixos e mora AQUI, do lado do componente, e não espalhada em cada página. Rota
 * nova ganha rodapé por padrão: se um dia alguma não deve ter, o lugar de dizer isso é esta linha,
 * e a ausência fica visível em vez de silenciosa.
 */
const SEM_RODAPE = ["/meus-quadrinhos/rascunho"];

function ehTelaDeEditor(caminho: string): boolean {
  if (SEM_RODAPE.includes(caminho)) return true;
  // `/meus-quadrinhos/:id/editar` — o id varia, então compara pelo fim.
  return caminho.startsWith("/meus-quadrinhos/") && caminho.endsWith("/editar");
}

/** O rodapé do produto, montado uma vez no `App` e escondido nas telas de editor. */
export function RodapeGlobal() {
  const local = useLocation();
  if (ehTelaDeEditor(local.pathname)) return null;
  return <Rodape />;
}

export default Rodape;
